import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import Assessments from "@/pages/Assessments";
import Absenteeism from "@/pages/absenteeism/Absenteeism";
import RiskMapConsolidated from "@/pages/RiskMapConsolidated";
import AnnualPlan from "@/pages/AnnualPlan";
import CompanyForm from "@/pages/companies/CompanyForm";
import api from "@/lib/api";

/* =================================================================
   Sub-routes of /app/empresas/:id/*
   Para Fase 1, apenas Dashboard tem conteúdo executivo real.
   As 10 outras abas mostram um placeholder elegante que aponta para
   o módulo global correspondente (links já filtrados pela empresa).
   Fase 2 vai trazer cada módulo nativo aqui dentro.
   ================================================================= */

/* ------------------- DASHBOARD (executivo) ------------------- */
export function CompanyDashboard() {
  const { company, kpis } = useOutletContext();
  return (
    <div data-testid="company-dashboard" className="space-y-6">
      <section className="bg-white border border-slate-200 p-6">
        <div className="overline mb-2">Visão Executiva</div>
        <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 mb-3">
          Status atual de {company.razao_social}
        </h2>
        <p className="text-sm text-slate-600 leading-relaxed max-w-3xl">
          Este prontuário consolida em um só lugar avaliações de risco psicossocial, PGR, plano de ação,
          documentos, treinamentos e indicadores de conformidade NR-01 desta empresa. Use o menu lateral
          para navegar entre os blocos.
        </p>
      </section>

      <section className="bg-white border border-slate-200 p-6">
        <div className="overline mb-3">Resumo operacional</div>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <li className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-100">
            <i className="fa-solid fa-clipboard-list text-emerald-700 mt-0.5"></i>
            <div>
              <div className="font-semibold text-slate-900">{kpis?.avaliacoes || 0} avaliação(ões) realizada(s)</div>
              <div className="text-xs text-slate-500">Risco médio: {kpis?.risco_medio?.toFixed(2) || "—"} (escala 1-5)</div>
            </div>
          </li>
          <li className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-100">
            <i className="fa-solid fa-list-check text-amber-700 mt-0.5"></i>
            <div>
              <div className="font-semibold text-slate-900">{kpis?.acoes_pendentes || 0} ação(ões) pendente(s)</div>
              <div className="text-xs text-slate-500">Plano de ação em andamento</div>
            </div>
          </li>
          <li className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-100">
            <i className="fa-solid fa-circle-check text-sky-700 mt-0.5"></i>
            <div>
              <div className="font-semibold text-slate-900">
                Conformidade NR-01: {kpis?.conformidade_nr01 == null ? "Sem dados" : `${kpis.conformidade_nr01}%`}
              </div>
              <div className="text-xs text-slate-500">Calculado a partir das avaliações concluídas</div>
            </div>
          </li>
          <li className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-100">
            <i className="fa-solid fa-users text-slate-700 mt-0.5"></i>
            <div>
              <div className="font-semibold text-slate-900">{kpis?.colaboradores || 0} colaborador(es)</div>
              <div className="text-xs text-slate-500">Cadastrado(s) no perfil da empresa</div>
            </div>
          </li>
        </ul>
      </section>

      <section className="bg-white border border-slate-200 p-6">
        <div className="overline mb-3">Atalhos rápidos</div>
        <div className="flex flex-wrap gap-2">
          <Link to={`/app/avaliacoes?company_id=${company.id}`} className="btn btn-primary text-xs" data-testid="hub-shortcut-new-assessment">
            <i className="fa-solid fa-plus"></i> Nova avaliação
          </Link>
          <Link to={`/app/empresas/${company.id}/mapa-risco`} className="btn btn-secondary text-xs" data-testid="hub-shortcut-risk-map">
            <i className="fa-solid fa-shield-halved"></i> Mapa de risco
          </Link>
          <Link to={`/app/empresas/${company.id}/plano-anual`} className="btn btn-ghost text-xs border border-emerald-300 text-emerald-900 hover:bg-emerald-50" data-testid="hub-shortcut-annual-plan">
            <i className="fa-solid fa-calendar-check"></i> Plano anual
          </Link>
          <Link to={`/app/importar/pgr/${company.id}`} className="btn btn-ghost text-xs border border-violet-300 text-violet-900 hover:bg-violet-50" data-testid="hub-shortcut-pgr">
            <i className="fa-solid fa-wand-magic-sparkles"></i> Importar PGR
          </Link>
        </div>
      </section>
    </div>
  );
}

/* ------------------- Placeholder genérico ------------------- */
function Placeholder({ icon, title, description, action }) {
  return (
    <div className="bg-white border border-slate-200 p-10 text-center" data-testid={`hub-placeholder-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <i className={`fa-solid ${icon} text-4xl text-slate-300 mb-4`}></i>
      <h2 className="font-display text-xl font-extrabold text-slate-900 mb-2">{title}</h2>
      <p className="text-sm text-slate-600 max-w-md mx-auto mb-5">{description}</p>
      {action}
      <p className="text-[11px] text-slate-400 mt-6">
        <i className="fa-solid fa-info-circle mr-1"></i>
        Integração nativa nesta tela disponível na próxima atualização.
      </p>
    </div>
  );
}

/* ------------------- Sub-rotas com módulo nativo (Fase 2) ------------------- */
export function CompanyAvaliacoes() {
  const { company } = useOutletContext();
  return <Assessments companyIdOverride={company.id} />;
}

export function CompanyRiscos() {
  const { company } = useOutletContext();
  return <RiskMapConsolidated companyIdOverride={company.id} />;
}

export function CompanyPgr() {
  const { company } = useOutletContext();
  return (
    <Placeholder
      icon="fa-file-shield"
      title="PGR — Programa de Gerenciamento de Riscos"
      description="Importar PGR existente via IA, enriquecer dados da empresa ou gerar PGR psicossocial."
      action={
        <Link to={`/app/importar/pgr/${company.id}`} className="btn btn-primary" data-testid="placeholder-go-pgr">
          <i className="fa-solid fa-wand-magic-sparkles"></i> Analisar PGR
        </Link>
      }
    />
  );
}

export function CompanyPlanoAcao() {
  const { company } = useOutletContext();
  return <AnnualPlan companyIdOverride={company.id} />;
}

export function CompanyAbsenteismo() {
  const { company } = useOutletContext();
  return <Absenteeism companyIdOverride={company.id} />;
}

/* ------------------- Ainda placeholder (Fase 3) ------------------- */

/* ============================================================
   FASE 3 — Documentos · Downloads · Relatórios · Histórico · Config
   Implementadas nativamente reusando APIs existentes.
   ============================================================ */

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("pt-BR") : "—";
const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString("pt-BR") : "—";

/* ----------------- Documentos (prontuário) ----------------- */
const DOC_TYPES = [
  { code: "pgr",     label: "PGR — Programa de Gerenciamento de Riscos", icon: "fa-file-shield",     color: "emerald" },
  { code: "pcmso",   label: "PCMSO — Programa de Controle Médico",       icon: "fa-stethoscope",     color: "rose" },
  { code: "ltcat",   label: "LTCAT — Laudo Técnico de Cond. Ambientais", icon: "fa-microscope",      color: "amber" },
  { code: "ppp",     label: "PPP — Perfil Profissiográfico",             icon: "fa-id-card-clip",    color: "sky" },
  { code: "aet",     label: "AET — Análise Ergonômica do Trabalho",      icon: "fa-person-running",  color: "violet" },
  { code: "aso",     label: "ASO — Atestado de Saúde Ocupacional",       icon: "fa-notes-medical",   color: "rose" },
  { code: "laudos",  label: "Laudos técnicos diversos",                  icon: "fa-file-circle-check", color: "slate" },
  { code: "trein",   label: "Certificados de Treinamento",               icon: "fa-graduation-cap",  color: "amber" },
];

export function CompanyDocumentos() {
  const { company } = useOutletContext();
  return (
    <div className="space-y-6" data-testid="company-documentos">
      <section className="bg-white border border-slate-200 p-6">
        <div className="overline mb-2">Prontuário documental</div>
        <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Documentos de {company.razao_social}</h2>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Documentos legais obrigatórios da empresa. Para upload e versionamento controlado, use o módulo administrativo
          de Materiais e marque com a categoria do tipo de documento.
        </p>
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {DOC_TYPES.map((d) => (
          <div key={d.code} className="bg-white border border-slate-200 p-4 hover:border-emerald-300 transition-colors" data-testid={`doc-card-${d.code}`}>
            <div className={`w-10 h-10 inline-flex items-center justify-center mb-3 bg-${d.color}-100 text-${d.color}-700`}>
              <i className={`fa-solid ${d.icon}`}></i>
            </div>
            <div className="font-semibold text-slate-900 text-sm leading-tight mb-1">{d.label}</div>
            <div className="text-[11px] text-slate-500 mb-3">Status: <span className="text-amber-700 font-semibold">Não anexado</span></div>
            <Link to="/app/admin/downloads" className="text-[11px] text-emerald-800 hover:underline">
              <i className="fa-solid fa-cloud-arrow-up"></i> Fazer upload
            </Link>
          </div>
        ))}
      </section>

      <div className="bg-slate-50 border border-slate-200 p-4 text-xs text-slate-600">
        <i className="fa-solid fa-info-circle text-emerald-700 mr-1"></i>
        <strong>Versionamento automático em breve.</strong> Hoje os documentos compartilhados ficam no módulo &quot;Materiais&quot; (admin).
        A próxima evolução vincula uploads diretamente a esta empresa com histórico de revisões.
      </div>
    </div>
  );
}

/* ----------------- Downloads (filtrado por empresa via tag) ----------------- */
export function CompanyDownloads() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paywall, setPaywall] = useState(false);

  useEffect(() => {
    api.get("/downloads")
      .then(({ data }) => setItems(data))
      .catch((err) => {
        if (err.response?.status === 402) setPaywall(true);
        else toast.error(err.response?.data?.detail || err.message);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="bg-white border border-slate-200 p-10 text-center text-slate-500" data-testid="company-downloads-loading"><i className="fa-solid fa-spinner fa-spin mr-2"></i>Carregando…</div>;
  if (paywall) return (
    <div className="bg-amber-50 border border-amber-200 p-6" data-testid="company-downloads-paywall">
      <i className="fa-solid fa-lock text-amber-700 mr-2"></i>
      Materiais exclusivos do plano completo. <Link to="/app/planos" className="underline font-semibold">Conheça os planos</Link>.
    </div>
  );

  return (
    <div className="space-y-4" data-testid="company-downloads">
      <section className="bg-white border border-slate-200 p-6">
        <div className="overline mb-2">Biblioteca</div>
        <h2 className="font-display text-2xl font-extrabold text-slate-900">Materiais para download</h2>
        <p className="text-sm text-slate-600 mt-2">{items.length} material(is) disponível(is) para uso nesta empresa.</p>
      </section>
      {items.length === 0 ? (
        <div className="bg-white border border-slate-200 p-10 text-center text-slate-500" data-testid="company-downloads-empty">
          <i className="fa-solid fa-folder-open text-3xl mb-3 text-slate-300"></i>
          <p className="text-sm">Nenhum material publicado ainda.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((m) => (
            <a
              key={m.id}
              href={`${BACKEND}/api/downloads/${m.id}/file`}
              target="_blank"
              rel="noreferrer"
              className="bg-white border border-slate-200 hover:border-emerald-300 p-4 flex items-center gap-3 transition-colors"
              data-testid={`company-download-${m.id}`}
            >
              <i className={`fa-solid ${m.icon || "fa-file"} text-2xl text-emerald-700`}></i>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-slate-900 truncate">{m.title}</div>
                <div className="text-[11px] text-slate-500 uppercase tracking-wider font-mono">.{m.ext} · {m.size_human} · {m.category}</div>
              </div>
              <i className="fa-solid fa-download text-slate-400"></i>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

/* ----------------- Relatórios (central de PDFs/exports) ----------------- */
function ReportCell({ icon, color, title, desc, to, testid, badge }) {
  return (
    <Link to={to} data-testid={testid} className="bg-white border border-slate-200 hover:border-emerald-300 p-5 flex gap-4 items-start transition-colors group">
      <div className={`w-12 h-12 flex-none inline-flex items-center justify-center bg-${color}-100 text-${color}-700`}>
        <i className={`fa-solid ${icon} text-xl`}></i>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="font-semibold text-slate-900 group-hover:text-emerald-800">{title}</div>
          {badge != null && <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-900 px-1.5 py-0.5">{badge}</span>}
        </div>
        <div className="text-xs text-slate-600 leading-snug">{desc}</div>
      </div>
      <i className="fa-solid fa-arrow-right text-slate-300 group-hover:text-emerald-700 mt-3"></i>
    </Link>
  );
}

export function CompanyRelatorios() {
  const { company } = useOutletContext();
  const [counts, setCounts] = useState({ assessments: 0, plans: 0 });

  useEffect(() => {
    Promise.all([
      api.get("/assessments").catch(() => ({ data: [] })),
      api.get("/action-plans").catch(() => ({ data: [] })),
    ]).then(([a, p]) => {
      const myA = a.data.filter((x) => x.company_id === company.id);
      const myP = p.data.filter((x) => x.company_id === company.id);
      setCounts({ assessments: myA.length, plans: myP.length });
    });
  }, [company.id]);

  return (
    <div className="space-y-4" data-testid="company-relatorios">
      <section className="bg-white border border-slate-200 p-6">
        <div className="overline mb-2">Central de relatórios</div>
        <h2 className="font-display text-2xl font-extrabold text-slate-900">Relatórios de {company.razao_social}</h2>
        <p className="text-sm text-slate-600 mt-2">Acesse aqui todos os relatórios técnicos, executivos e gerenciais desta empresa.</p>
      </section>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ReportCell
          icon="fa-file-pdf"      color="emerald"  title="AEP / Avaliações"        badge={counts.assessments}
          desc="Relatório técnico (PDF) por avaliação concluída — escala, dimensões e plano de ação sugerido."
          to={`/app/empresas/${company.id}/avaliacoes`} testid="rep-aep" />
        <ReportCell
          icon="fa-chart-pie"     color="sky"      title="Dashboard Avançado"
          desc="Visão analítica com filtros cruzados, tendências e export Excel/CSV."
          to={`/app/dashboard-avancado?company_id=${company.id}`} testid="rep-advanced" />
        <ReportCell
          icon="fa-shield-halved" color="amber"    title="Mapa de Risco Consolidado"
          desc="Heatmap por GHE cruzando NR-01 psicossocial × absenteísmo × ações pendentes."
          to={`/app/empresas/${company.id}/riscos`} testid="rep-risk" />
        <ReportCell
          icon="fa-user-clock"    color="rose"     title="Relatório de Absenteísmo"
          desc="CIDs (F/M), dias perdidos, taxa de absenteísmo, indicadores epidemiológicos."
          to={`/app/empresas/${company.id}/absenteismo`} testid="rep-abs" />
        <ReportCell
          icon="fa-calendar-check" color="emerald" title="Plano Anual SST"          badge={counts.plans}
          desc="Cronograma anual com campanhas, DDS, inspeções, treinamentos — exportável em PDF/CSV."
          to={`/app/empresas/${company.id}/plano-acao`} testid="rep-plan" />
        <ReportCell
          icon="fa-magnifying-glass-chart" color="violet" title="Auditoria & Conformidade"
          desc="Análise NR-01 com indicadores de conformidade legal e gaps documentais."
          to={`/app/empresas/${company.id}/dashboard`} testid="rep-audit" />
      </div>
    </div>
  );
}

/* ----------------- Histórico (timeline cronológica) ----------------- */
export function CompanyHistorico() {
  const { company } = useOutletContext();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/assessments").catch(() => ({ data: [] })),
      api.get("/action-plans").catch(() => ({ data: [] })),
    ])
      .then(([a, p]) => {
        const items = [];
        a.data.filter((x) => x.company_id === company.id).forEach((it) => {
          items.push({
            id: `a-${it.id}`,
            ts: it.created_at,
            icon: "fa-clipboard-list", color: "emerald",
            title: `Avaliação criada: ${it.titulo}`,
            sub: `Instrumento ${(it.test_type || "nr01_54").toUpperCase()} · ${it.response_count ?? 0} respondente(s)`,
            link: `/app/avaliacoes/${it.id}`,
          });
          if (it.completed_at) items.push({
            id: `ac-${it.id}`,
            ts: it.completed_at,
            icon: "fa-check-circle", color: "sky",
            title: `Avaliação concluída: ${it.titulo}`,
            sub: `Score global ${it.global_score?.toFixed?.(2) || "—"}`,
            link: `/app/avaliacoes/${it.id}`,
          });
        });
        p.data.filter((x) => x.company_id === company.id).forEach((it) => {
          items.push({
            id: `p-${it.id}`,
            ts: it.created_at,
            icon: "fa-list-check", color: it.status === "concluido" ? "emerald" : "amber",
            title: `Ação: ${it.title || it.descricao || "Plano de ação"}`,
            sub: `Status: ${it.status || "pendente"}${it.due_date ? ` · prazo ${fmtDate(it.due_date)}` : ""}`,
            link: `/app/empresas/${company.id}/plano-acao`,
          });
        });
        items.sort((x, y) => (new Date(y.ts || 0)) - (new Date(x.ts || 0)));
        setEvents(items);
      })
      .finally(() => setLoading(false));
  }, [company.id]);

  return (
    <div className="space-y-4" data-testid="company-historico">
      <section className="bg-white border border-slate-200 p-6">
        <div className="overline mb-2">Timeline</div>
        <h2 className="font-display text-2xl font-extrabold text-slate-900">Histórico de {company.razao_social}</h2>
        <p className="text-sm text-slate-600 mt-2">{events.length} evento(s) registrado(s).</p>
      </section>
      {loading ? (
        <div className="bg-white border border-slate-200 p-10 text-center text-slate-500"><i className="fa-solid fa-spinner fa-spin mr-2"></i>Carregando timeline…</div>
      ) : events.length === 0 ? (
        <div className="bg-white border border-slate-200 p-10 text-center text-slate-500" data-testid="company-historico-empty">
          <i className="fa-solid fa-clock-rotate-left text-3xl mb-3 text-slate-300"></i>
          <p className="text-sm">Nenhum evento registrado ainda nesta empresa.</p>
        </div>
      ) : (
        <ol className="relative border-l-2 border-slate-200 ml-3 space-y-4">
          {events.map((ev) => (
            <li key={ev.id} className="ml-6" data-testid={`event-${ev.id}`}>
              <span className={`absolute -left-[11px] flex w-5 h-5 items-center justify-center rounded-full bg-${ev.color}-600 text-white`} style={{ marginTop: 2 }}>
                <i className={`fa-solid ${ev.icon} text-[9px]`}></i>
              </span>
              <div className="bg-white border border-slate-200 p-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <Link to={ev.link} className="font-semibold text-sm text-slate-900 hover:text-emerald-800">{ev.title}</Link>
                  <span className="text-[11px] text-slate-500 font-mono">{fmtDateTime(ev.ts)}</span>
                </div>
                <div className="text-xs text-slate-600 mt-1">{ev.sub}</div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/* ----------------- Configurações (editar empresa inline) ----------------- */
export function CompanyConfig() {
  const { company, reload } = useOutletContext();
  const handleSubmit = async (form) => {
    try {
      await api.put(`/companies/${company.id}`, form);
      toast.success("Empresa atualizada");
      if (reload) await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };
  return (
    <div className="space-y-4" data-testid="company-config">
      <section className="bg-white border border-slate-200 p-6">
        <div className="overline mb-2">Configurações da empresa</div>
        <h2 className="font-display text-2xl font-extrabold text-slate-900">Editar dados cadastrais</h2>
        <p className="text-sm text-slate-600 mt-2">Atualize razão social, CNPJ, CNAE, grau de risco, endereço, responsável técnico e logo institucional.</p>
      </section>
      <div className="bg-white border border-slate-200 p-6">
        <CompanyForm initial={company} onSubmit={handleSubmit} onCancel={() => {}} />
      </div>
    </div>
  );
}
